// Entitlement provider abstraction — Stripe (web/PWA) plus RevenueCat-backed
// StoreKit (iOS) and Play Billing (Android) for native shells. Native
// adapters require RevenueCat dashboard config + matching products in App
// Store Connect / Play Console. Missing SDK keys degrade to
// { tier: 'free', active: false, source: 'none' } — no crash.

export type EntitlementTier = "free" | "member" | "founder";

export type EntitlementStatus = {
  tier: EntitlementTier;
  active: boolean;
  source: "stripe" | "storekit" | "play" | "none";
  renewsAt?: string | null;
  expiresAt?: string | null;
};

export interface EntitlementProvider {
  readonly id: "stripe" | "storekit" | "play";
  /** Fetch entitlement status for the current authenticated user. */
  getStatus(): Promise<EntitlementStatus>;
  /** Kick off a purchase flow for the given product id. */
  purchase(productId: string): Promise<{ ok: boolean; reason?: string }>;
  /** Restore prior purchases (native stores). No-op for Stripe. */
  restore(): Promise<{ ok: boolean; reason?: string }>;

  /** Open management surface (Stripe portal / native subscription mgmt). */
  manage(): Promise<{ url?: string }>;
}

/**
 * Choose the active provider based on runtime.
 * - Capacitor iOS → StoreKit (when adapter is activated)
 * - Capacitor Android → Play Billing (when adapter is activated)
 * - Web / PWA → Stripe
 */
export async function getEntitlementProvider(): Promise<EntitlementProvider> {
  if (typeof window !== "undefined") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cap = (window as any).Capacitor;
    if (cap?.isNativePlatform?.()) {
      const platform = cap.getPlatform?.();
      if (platform === "ios") {
        const { storeKitProvider } = await import("./storekit");
        return storeKitProvider;
      }
      if (platform === "android") {
        const { playBillingProvider } = await import("./play");
        return playBillingProvider;
      }
    }
  }
  const { stripeProvider } = await import("./stripe");
  return stripeProvider;
}
